import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../../data/mock';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

export const CTASection = () => {
  const [ref, isInView] = useInViewOnce();

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello SAX! I'm interested in discussing a potential project with your team.");
    const whatsappUrl = `https://wa.me/${mockData.company.whatsapp.replace(/\s+/g, '').replace(/^\+/, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${mockData.company.phone}`, '_self');
  };

  return (
    <section className="py-20 bg-gradient-to-br from-[#1F3C88] to-[#2d4ba3] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/10" />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 right-20 w-32 h-32 bg-gradient-to-r from-[#FF5C8A]/20 to-[#FF8C42]/20 rounded-full blur-xl" />
      <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full blur-lg" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-8 backdrop-blur-sm"
          >
            <span>Get Started Today</span>
          </motion.div>

          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {mockData.ctaBanners[3]}
          </motion.h2>

          <motion.p 
            variants={revealVariants}
            className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Transform your vision into reality with our multi-sector expertise. 
            Let's discuss how SAX can deliver measurable results for your next project.
          </motion.p>

          <motion.div 
            variants={revealVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
          >
            <Link
              to="/request-service"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <span>Request a Proposal</span>
              <ArrowRight size={20} />
            </Link>

            <button
              onClick={handleCallClick}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/30 transition-all duration-300 border border-white/30"
            >
              <Phone size={20} />
              <span>Call Now</span>
            </button>
          </motion.div>

          {/* Quick Contact Options */}
          <motion.div 
            variants={revealVariants}
            className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8 text-blue-100"
          >
            <div className="flex items-center space-x-2">
              <MessageCircle size={20} className="text-[#FF5C8A]" />
              <span>WhatsApp: </span>
              <button
                onClick={handleWhatsAppClick}
                className="font-semibold hover:text-white transition-colors"
              >
                {mockData.company.whatsapp}
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <Phone size={20} className="text-[#FF5C8A]" />
              <span>Call: </span>
              <button
                onClick={handleCallClick}
                className="font-semibold hover:text-white transition-colors"
              >
                {mockData.company.phone}
              </button>
            </div>
          </motion.div>

          {/* Guarantee Badge */}
          <motion.div
            variants={revealVariants}
            className="mt-12 inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6"
          >
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full flex items-center justify-center">
                <div className="text-white font-bold">✓</div>
              </div>
              <div className="text-left">
                <div className="text-white font-semibold">Quality Guarantee</div>
                <div className="text-blue-100 text-sm">On-time delivery • Budget adherence • Measurable results</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};